import { z } from 'zod'

export const MeetingWeekdaySchema = z.enum([
  'monday',
  'tuesday',
  'wednesday',
  'thursday',
  'friday',
  'saturday',
  'sunday',
])
export type MeetingWeekday = z.infer<typeof MeetingWeekdaySchema>

export const MeetingLocalDateSchema = z
  .string()
  .regex(/^\d{4}-\d{2}-\d{2}$/, 'startDate must be YYYY-MM-DD')
  .refine((date) => {
    const [year, month, day] = date.split('-').map(Number)
    const parsed = new Date(Date.UTC(year, month - 1, day))
    return parsed.getUTCFullYear() === year && parsed.getUTCMonth() === month - 1 && parsed.getUTCDate() === day
  }, 'startDate must be a valid calendar date')
  .meta({
    id: 'meetingLocalDate',
    description: 'Team-local calendar date that anchors recurrence intervals',
    example: '2026-09-07',
  })
export type MeetingLocalDate = z.infer<typeof MeetingLocalDateSchema>

const MeetingScheduleStartSchema = z.number().int().min(0).max(1439).meta({
  id: 'meetingScheduleStart',
  description: 'Meeting start time in minutes since midnight in the team timezone',
  example: 540,
})

const MeetingScheduleDurationSchema = z.number().int().positive().meta({
  id: 'meetingScheduleDuration',
  description: 'Meeting duration in minutes',
  example: 60,
})

const MeetingScheduleIntervalSchema = z.number().int().positive().max(52).meta({
  id: 'meetingScheduleInterval',
  description: 'Recurrence interval (every N weeks or months)',
  example: 1,
})

export const MeetingWeeklyScheduleRuleSchema = z.object({
  frequency: z.literal('weekly'),
  interval: MeetingScheduleIntervalSchema,
  startDate: MeetingLocalDateSchema,
  days: MeetingWeekdaySchema.array()
    .min(1)
    .refine((days) => new Set(days).size === days.length, 'days must be unique'),
  start: MeetingScheduleStartSchema,
  duration: MeetingScheduleDurationSchema,
})
export type MeetingWeeklyScheduleRule = z.infer<typeof MeetingWeeklyScheduleRuleSchema>

export const MeetingMonthlyScheduleRuleSchema = z.object({
  frequency: z.literal('monthly'),
  interval: MeetingScheduleIntervalSchema,
  startDate: MeetingLocalDateSchema,
  ordinal: z.union([z.literal(1), z.literal(2), z.literal(3), z.literal(4), z.literal(-1)]).meta({
    id: 'meetingMonthlyOrdinal',
    description: 'Which weekday occurrence in the month. 1–4 are the first through fourth; -1 is the last.',
    example: 1,
  }),
  weekday: MeetingWeekdaySchema,
  start: MeetingScheduleStartSchema,
  duration: MeetingScheduleDurationSchema,
})
export type MeetingMonthlyScheduleRule = z.infer<typeof MeetingMonthlyScheduleRuleSchema>

export const MeetingScheduleRuleSchema = z.discriminatedUnion('frequency', [
  MeetingWeeklyScheduleRuleSchema,
  MeetingMonthlyScheduleRuleSchema,
])
export type MeetingScheduleRule = z.infer<typeof MeetingScheduleRuleSchema>
